import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import './AvailabilityPicker.css';

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

export const MAX_SLOTS = 10;
export const INTERVIEW_DURATION_MINUTES = 30;
const VISIBLE_DAYS = 8;

export const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)', short: 'ET' },
  { value: 'America/Chicago', label: 'Central (CT)', short: 'CT' },
  { value: 'America/Denver', label: 'Mountain (MT)', short: 'MT' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)', short: 'PT' },
];

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function generateTimeSlots(startHour, endHourExclusive) {
  const out = [];
  for (let h = startHour; h < endHourExclusive; h += 1) {
    out.push(`${String(h).padStart(2, '0')}:00`);
    out.push(`${String(h).padStart(2, '0')}:30`);
  }
  return out;
}

const CORE_HOURS = generateTimeSlots(8, 18); // 8:00 to 17:30
const EARLY_HOURS = generateTimeSlots(6, 8); // 6:00 to 7:30
const LATE_HOURS = generateTimeSlots(18, 21); // 18:00 to 20:30

function pad(n) {
  return String(n).padStart(2, '0');
}

export function dateKey(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function parseDateKey(key) {
  // Construct in local time to avoid UTC-shift surprises in display.
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function tomorrow() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return d;
}

function formatDayLabel(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { weekday: 'short' });
}

function formatDayDate(date) {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export function formatTimeLabel(time24) {
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${pad(m)} ${period}`;
}

export function deriveEndTime(startTime) {
  const [h, m] = startTime.split(':').map(Number);
  const total = h * 60 + m + INTERVIEW_DURATION_MINUTES;
  if (total >= 24 * 60) return '23:59';
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

export function pickDefaultUsTimezone() {
  let browserTz = '';
  try {
    browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {
    browserTz = '';
  }
  const map = {
    'America/New_York': 'America/New_York',
    'America/Detroit': 'America/New_York',
    'America/Indiana/Indianapolis': 'America/New_York',
    'America/Kentucky/Louisville': 'America/New_York',
    'America/Toronto': 'America/New_York',
    'America/Chicago': 'America/Chicago',
    'America/Mexico_City': 'America/Chicago',
    'America/Winnipeg': 'America/Chicago',
    'America/Denver': 'America/Denver',
    'America/Phoenix': 'America/Denver',
    'America/Edmonton': 'America/Denver',
    'America/Los_Angeles': 'America/Los_Angeles',
    'America/Vancouver': 'America/Los_Angeles',
    'America/Tijuana': 'America/Los_Angeles',
  };
  return map[browserTz] || 'America/New_York';
}

/**
 * Expand the parent's compact slot list into the backend DTO shape:
 * { date, startTime, endTime, timezone }, sorted chronologically.
 * Shared so both shells (wizard Step 2, in-negotiation modal) submit an
 * identical payload.
 */
export function expandSlots(slots, timezone) {
  return [...slots]
    .sort((a, b) => {
      if (a.dateKey !== b.dateKey) return a.dateKey.localeCompare(b.dateKey);
      return a.startTime.localeCompare(b.startTime);
    })
    .map((s) => ({
      date: s.dateKey,
      startTime: s.startTime,
      endTime: deriveEndTime(s.startTime),
      timezone,
    }));
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * AvailabilityPicker
 *
 * Controlled timezone + day-strip + 30-minute slot-grid + "Your picks"
 * summary. The parent owns `slots` and `timezone`; this component reports
 * every change up through onSlotsChange / onTimezoneChange and owns NO
 * submit chrome of its own. It is mounted in two shells: the swipe-wizard
 * Step 2, and the plain in-negotiation request-an-interview modal.
 *
 * slots shape: [{ dateKey: 'YYYY-MM-DD', startTime: 'HH:MM' }]
 */
export default function AvailabilityPicker({
  slots,
  onSlotsChange,
  timezone,
  onTimezoneChange,
}) {
  // Day strip starts at tomorrow + stripOffset days; it pages 8 at a time via
  // the < / > arrows. selectedDayKey is the day the time grid renders. These
  // are purely presentational, so they stay component-local.
  const [stripOffset, setStripOffset] = useState(0);
  const [selectedDayKey, setSelectedDayKey] = useState(() =>
    dateKey(tomorrow()),
  );
  const [showExtraHours, setShowExtraHours] = useState(false);

  const dayStrip = useMemo(() => {
    const base = tomorrow();
    return Array.from({ length: VISIBLE_DAYS }, (_, i) => {
      const d = new Date(base);
      d.setDate(d.getDate() + stripOffset + i);
      return d;
    });
  }, [stripOffset]);

  const slotsByDay = useMemo(() => {
    const map = {};
    slots.forEach((s) => {
      map[s.dateKey] = (map[s.dateKey] || 0) + 1;
    });
    return map;
  }, [slots]);

  const visibleHours = useMemo(() => {
    if (showExtraHours) return [...EARLY_HOURS, ...CORE_HOURS, ...LATE_HOURS];
    return CORE_HOURS;
  }, [showExtraHours]);

  const sortedSlots = useMemo(
    () =>
      [...slots].sort((a, b) => {
        if (a.dateKey !== b.dateKey) return a.dateKey.localeCompare(b.dateKey);
        return a.startTime.localeCompare(b.startTime);
      }),
    [slots],
  );

  const tzShort = US_TIMEZONES.find((t) => t.value === timezone)?.short || '';

  const isTimeSelected = useCallback(
    (time) =>
      slots.some((s) => s.dateKey === selectedDayKey && s.startTime === time),
    [slots, selectedDayKey],
  );

  const toggleTimeSlot = useCallback(
    (time) => {
      const exists = slots.find(
        (s) => s.dateKey === selectedDayKey && s.startTime === time,
      );
      if (exists) {
        onSlotsChange(
          slots.filter(
            (s) => !(s.dateKey === selectedDayKey && s.startTime === time),
          ),
        );
        return;
      }
      if (slots.length >= MAX_SLOTS) return;
      onSlotsChange([...slots, { dateKey: selectedDayKey, startTime: time }]);
    },
    [slots, selectedDayKey, onSlotsChange],
  );

  const removeSlot = useCallback(
    (slot) => {
      onSlotsChange(
        slots.filter(
          (s) => !(s.dateKey === slot.dateKey && s.startTime === slot.startTime),
        ),
      );
    },
    [slots, onSlotsChange],
  );

  return (
    <div className="ir-availability">
      {/* Timezone — a quiet, tappable line that opens a native select. */}
      <label className="ir-tz">
        <span className="ir-tz-icon" aria-hidden="true">
          🕐
        </span>
        <span className="ir-tz-text">
          Times shown in <strong>{US_TIMEZONES.find((t) => t.value === timezone)?.label || 'Eastern (ET)'}</strong>
        </span>
        <select
          className="ir-tz-select"
          value={timezone}
          onChange={(e) => onTimezoneChange(e.target.value)}
          aria-label="Timezone"
        >
          {US_TIMEZONES.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
      </label>

      {/* Day strip */}
      <div className="ir-strip-wrap">
        <button
          type="button"
          className="ir-strip-nav"
          onClick={() => setStripOffset((s) => Math.max(0, s - VISIBLE_DAYS))}
          disabled={stripOffset === 0}
          aria-label="Previous days"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path
              d="M9 2 L4 7 L9 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className="ir-strip" role="tablist">
          {dayStrip.map((d) => {
            const key = dateKey(d);
            const isSelected = key === selectedDayKey;
            const count = slotsByDay[key] || 0;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={isSelected}
                onClick={() => setSelectedDayKey(key)}
                className={[
                  'ir-day',
                  isSelected ? 'is-selected' : '',
                  count > 0 ? 'has-slots' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className="ir-day-label">{formatDayLabel(d)}</span>
                <span className="ir-day-date">{formatDayDate(d)}</span>
                {count > 0 && (
                  <span
                    className="ir-day-badge"
                    aria-label={`${count} slot${count > 1 ? 's' : ''}`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="ir-strip-nav"
          onClick={() => setStripOffset((s) => s + VISIBLE_DAYS)}
          aria-label="Next days"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path
              d="M5 2 L10 7 L5 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Time grid */}
      <div className="ir-grid-wrap">
        <div className="ir-grid">
          {visibleHours.map((time) => {
            const selected = isTimeSelected(time);
            return (
              <button
                key={time}
                type="button"
                onClick={() => toggleTimeSlot(time)}
                className={`ir-cell ${selected ? 'is-selected' : ''}`}
                aria-pressed={selected}
              >
                <span className="ir-cell-time">{formatTimeLabel(time)}</span>
                {selected && (
                  <svg
                    className="ir-cell-check"
                    width="11"
                    height="11"
                    viewBox="0 0 11 11"
                    aria-hidden="true"
                  >
                    <path
                      d="M1.5 5.5 L4 8 L9.5 2.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="ir-show-extra"
          onClick={() => setShowExtraHours((v) => !v)}
        >
          {showExtraHours
            ? 'Hide earlier & later hours'
            : '+ Show earlier & later hours'}
        </button>
      </div>

      {/* Your picks */}
      {sortedSlots.length > 0 && (
        <div className="ir-picks">
          <div className="ir-picks-head">
            Your picks
            <span className="ir-count">
              {' · '}
              {sortedSlots.length} time{sortedSlots.length === 1 ? '' : 's'}
            </span>
          </div>
          <ul className="ir-picks-list">
            {sortedSlots.map((s) => {
              const d = parseDateKey(s.dateKey);
              return (
                <li key={`${s.dateKey}-${s.startTime}`} className="ir-pp">
                  <span className="ir-pp-text">
                    {d.toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })}
                    {' · '}
                    {formatTimeLabel(s.startTime)}
                    {tzShort ? ` ${tzShort}` : ''}
                  </span>
                  <button
                    type="button"
                    className="ir-pp-remove"
                    onClick={() => removeSlot(s)}
                    aria-label="Remove time"
                  >
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      aria-hidden="true"
                    >
                      <path
                        d="M1 1 L9 9 M9 1 L1 9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

AvailabilityPicker.propTypes = {
  slots: PropTypes.arrayOf(
    PropTypes.shape({
      dateKey: PropTypes.string.isRequired,
      startTime: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onSlotsChange: PropTypes.func.isRequired,
  timezone: PropTypes.string.isRequired,
  onTimezoneChange: PropTypes.func.isRequired,
};
